import type { Dispatch, SetStateAction } from 'react'
import React from 'react'
import type { FilterState } from '@pages/ApprovalRequests/RequestsList/Filter/type'
import type { Status, SortOrder } from '@type/common'

/**
 * Per-workflow column preferences. Both `order` and `hidden` are intentionally
 * graceful: columns missing from `order` are appended at the end in their
 * default position (so newly-added workflow fields remain visible), and IDs in
 * `order`/`hidden` that no longer exist are simply ignored.
 */
export interface ColumnPreference {
  order?: string[]
  hidden?: string[]
}

/** Keyed by workflow id; the special key `default` is used when no workflow is selected. */
export type ColumnPreferences = Record<string, ColumnPreference>

export const DEFAULT_COLUMN_PREFS_KEY = 'default'

/**
 * Baseline layout applied to any prefs slot with no saved customisation.
 * `visible` is a whitelist: every other column — including a selected form's
 * dynamic field columns — starts hidden and is opted into via the Columns
 * manager. When set, the active slot also follows the top form selection
 * (`workflowContext`), so each form keeps its own additions. Absent → an
 * unsaved slot shows everything (the main page's behavior).
 */
export interface DefaultColumnLayout {
  order: string[]
  visible: string[]
}

/**
 * Lightweight description of the columns currently rendered by the table. Published
 * by `TableView` so that UI rendered higher up in the tree (e.g. the page header
 * Columns trigger) can manage them without prop-drilling react-table column defs.
 */
export interface ColumnMetadata {
  id: string
  label: string
}

interface DefaultProps {
  filters: FilterState
  sorting: Record<string, SortOrder>
  areFiltersCollapsed: boolean
  selectedRequestIds: { id: number; status: Status }[]
  columnPreferences: ColumnPreferences
  columnsMetadata: ColumnMetadata[]
  defaultColumnLayout?: DefaultColumnLayout
  // The provider's pinned filters, re-exposed so consumers can tell a
  // user-set filter apart from the surface's own scope (e.g. the dashboard
  // tab pins actionRequired, which must not count as "user is filtering").
  forcedFilters?: Partial<FilterState>
  // True when the list resolved to zero requests with no user-set filters —
  // published by TableView so chrome rendered above it (filter bar) can hide
  // itself while a zero-state card is shown instead of the table.
  isBlankSlate: boolean
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>
  setSorting: React.Dispatch<React.SetStateAction<Record<string, SortOrder>>>
  setSelectedRequestIds: Dispatch<SetStateAction<{ id: number; status: Status }[]>>
  setAreFiltersCollapsed: Dispatch<SetStateAction<boolean>>
  setColumnPreferences: Dispatch<SetStateAction<ColumnPreferences>>
  setColumnsMetadata: Dispatch<SetStateAction<ColumnMetadata[]>>
  setIsBlankSlate: Dispatch<SetStateAction<boolean>>
}

export const Default: DefaultProps = {
  filters: {
    status: '',
    date: {},
    completionDate: {},
    workflow: '',
    pipelineProcessName: '',
    actionRequired: false,
    requestor: '',
    approver: '',
    search: '',
    page: 1,
    limit: 1,
  },
  sorting: {},
  areFiltersCollapsed: false,
  selectedRequestIds: [],
  columnPreferences: {},
  columnsMetadata: [],
  isBlankSlate: false,
  setFilters: () => null,
  setSorting: () => null,
  setSelectedRequestIds: () => null,
  setAreFiltersCollapsed: () => null,
  setColumnPreferences: () => null,
  setColumnsMetadata: () => null,
  setIsBlankSlate: () => null,
}

export const Context = React.createContext(Default)

export default Context
